import { vlogCmsAllowed } from "@/lib/artistVlogAccess";
import { ArtistVlogPreview } from "@/components/video/ArtistVlogPreview";
import { useEffect, useRef, useState } from "react";
import {
  GoogleAuthProvider,
  onAuthStateChanged,
  signInWithPopup,
  signOut,
  User,
} from "firebase/auth";
import { auth } from "@/services/firebase/init";
import { artistHubEnabled } from "@/services/firebase/artistHub";
import {
  WorkspaceError,
  workspaceRequest,
} from "@/services/firebase/artistWorkspace";
import { Button } from "@/components/ui/button";
import ArtistWorkspace from "./ArtistWorkspace";
import ArtistDmPanel from "./ArtistDmPanel";
import ArtistDmInboxPanel from "./ArtistDmInboxPanel";

type Workspace = { artistId: string; displayName: string };
type Published = {
  contentId: string;
  title: string;
  body: string;
  type?: string;
  revisionId?: string;
  video?: {assetId: string; status: string};
  liked: boolean;
  saved: boolean;
};

// Separate entrypoint: creator membership never grants the site's CMS roles.
export default function CreatorPortal() {
  const [user, setUser] = useState<User | null>(null);
  const [ready, setReady] = useState(false);
  const [items, setItems] = useState<Workspace[]>([]);
  const [selected, setSelected] = useState<Workspace | null>(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [posts, setPosts] = useState<
    { contentId: string; title: string; preview?: string }[]
  >([]);
  const [vlogEnabled, setVlogEnabled] = useState(false);
  const [postType, setPostType] = useState<"post" | "vlog">("post");
  const [post, setPost] = useState<Published | null>(null);
  const epoch = useRef(0);
  const current = useRef<string | null>(null);
  const lock = useRef(false);
  const [cursor, setCursor] = useState<string | null>(null);
  useEffect(() => {
    const invalidate = () => {epoch.current++;};
    const previousTitle = document.title;
    document.title = "Reel+ 創作者工作區";
    document.documentElement.classList.add("reel-creator");
    const off = onAuthStateChanged(auth, (next) => {
      epoch.current++;
      lock.current = false;
      current.current = null;
      setUser(next);
      setReady(true);
      setItems([]);
      setSelected(null);
      setPosts([]);
      setVlogEnabled(false);
      setPostType("post");
      setPost(null);
      setError("");
      setLoading(false);
    });
    return () => {
      invalidate();
      off();
      document.documentElement.classList.remove("reel-creator");
      document.title = previousTitle;
    };
  }, []);
  async function run(fn: (generation: number) => Promise<void>) {
    if (lock.current) return;
    lock.current = true;
    setLoading(true);
    setError("");
    const gen = epoch.current;
    try {
      await fn(gen);
    } catch (e) {
      if (gen === epoch.current) {
        setError(e instanceof Error ? e.message : "操作未完成，請重試。");
        if (
          e instanceof WorkspaceError &&
          [
            "NOT_FOUND",
            "FORBIDDEN",
            "UNAUTHENTICATED",
            "FEATURE_NOT_READY",
          ].includes(e.code)
        ) {
          setPost(null);
          setPosts([]);
        }
      }
    } finally {
      if (gen === epoch.current) {
        lock.current = false;
        setLoading(false);
      }
    }
  }
  async function load(gen: number, more = false) {
    const page = await workspaceRequest<{
      items: Workspace[];
      nextCursor: string | null;
    }>("workspaceMine", "", { ...(more && cursor ? { cursor } : {}) });
    if (gen === epoch.current) {
      setItems((old) => (more ? [...old, ...page.items] : page.items));
      setCursor(page.nextCursor);
      if (!more && !page.items.some((a) => a.artistId === current.current)) {
        current.current = null;
        setSelected(null);
        setPosts([]);
        setPost(null);
      }
    }
  }
  const loadOnLogin = useRef(load);
  const runOnLogin = useRef(run);
  loadOnLogin.current = load;
  runOnLogin.current = run;
  useEffect(() => {
    if (user && !user.isAnonymous && artistHubEnabled)
      void runOnLogin.current((gen) => loadOnLogin.current(gen));
  }, [user]);
  async function readPosts(gen: number, artist: Workspace, type: "post" | "vlog" = postType) {
    if (gen !== epoch.current || current.current !== artist.artistId) return;
    setVlogEnabled(false);
    setPosts([]);
    setPost(null);
    const access = await workspaceRequest<{enabled: boolean; vlogCms?: boolean; vlogPublic?: boolean}>("contentAccess", artist.artistId);
    if (gen !== epoch.current || current.current !== artist.artistId) return;
    const allowed = vlogCmsAllowed(access) && access.vlogPublic === true;
    const effectiveType = allowed ? type : "post";
    const page = await workspaceRequest<{
      items: { contentId: string; title: string; preview?: string }[];
    }>("contentList", artist.artistId, { limit: 20, includePreview: true, type: effectiveType });
    if (gen === epoch.current && current.current === artist.artistId) {
      setVlogEnabled(allowed);
      setPostType(effectiveType);
      setPosts(page.items);
      setPost(null);
    }
  }
  function choose(artist: Workspace) {
    current.current = artist.artistId;
    setVlogEnabled(false);
    setPostType("post");
    setSelected(artist);
    setPost(null);
    setPosts([]);
    void run((gen) => readPosts(gen, artist, "post"));
  }
  function read(contentId: string) {
    if (!selected) return;
    void run(async (gen) => {
      const value = await workspaceRequest<Published>(
        "contentGet",
        selected.artistId,
        { contentId },
      );
      if (gen === epoch.current) setPost(value);
    });
  }
  function interact(action: "contentLike" | "contentSave", active: boolean) {
    if (!post || !selected) return;
    void run(async (gen) => {
      await workspaceRequest(action, selected.artistId, {
        contentId: post.contentId,
        active,
      });
      const value = await workspaceRequest<Published>(
        "contentGet",
        selected.artistId,
        { contentId: post.contentId },
      );
      if (gen === epoch.current) setPost(value);
    });
  }
  if (!artistHubEnabled)
    return <main className="p-8">此環境尚未開放創作者工作區。</main>;
  return (
    <main className="mx-auto min-h-screen max-w-3xl space-y-6 p-5 sm:p-10">
      <header className="flex flex-wrap items-center justify-between gap-4 border-b pb-6">
        <div>
          <h1 className="text-3xl font-semibold">Reel+</h1>
          <p className="mt-2 text-muted-foreground">
            創作者工作區 · Sandbox 試點
          </p>
        </div>
        {user && (
          <Button
            variant="outline"
            disabled={loading}
            onClick={() =>
              void run(async () => {
                await signOut(auth);
              })
            }
          >
            登出
          </Button>
        )}
      </header>
      {!ready ? (
        <p>載入中…</p>
      ) : !user || user.isAnonymous ? (
        <section className="space-y-4">
          <h2 className="text-xl">把故事之外的日常，留給喜歡你的人。</h2>
          <p>使用已獲授權的 Google 帳號登入。</p>
          <Button
            disabled={loading}
            onClick={() =>
              void run(async () => {
                await signInWithPopup(auth, new GoogleAuthProvider());
              })
            }
          >
            使用 Google 登入
          </Button>
        </section>
      ) : (
        <>
          <p className="break-all text-muted-foreground">{user.email}</p>
          <div className="flex flex-wrap gap-3">
            {items.map((a) => (
              <Button
                disabled={loading}
                key={a.artistId}
                variant={
                  selected?.artistId === a.artistId ? "default" : "outline"
                }
                onClick={() => choose(a)}
              >
                {a.displayName}
              </Button>
            ))}
          </div>
          {!loading && items.length === 0 && (
            <p>此帳號目前沒有藝人工作區授權。</p>
          )}
          {cursor && (
            <Button
              disabled={loading}
              onClick={() => void run((gen) => load(gen, true))}
            >
              更多工作區
            </Button>
          )}
          <Button
            variant="outline"
            disabled={loading}
            onClick={() => void run((gen) => load(gen))}
          >
            重新整理授權
          </Button>
          {selected && (
            <section key={selected.artistId} className="space-y-6">
              <h2 className="text-xl">{selected.displayName}</h2>
              <ArtistWorkspace artistId={selected.artistId} />
              {/* 鐵粉私訊：後端只允許該藝人的 artist／editor（reviewer 403，面板顯示無權限） */}
              <ArtistDmPanel artistId={selected.artistId} />
              <ArtistDmInboxPanel artistId={selected.artistId} />
              <div className="flex flex-wrap items-center justify-between gap-3">
                <h3 className="text-lg">粉絲閱讀預覽</h3>
                <Button
                  variant="outline"
                  disabled={loading}
                  onClick={() => void run((gen) => readPosts(gen, selected))}
                >
                  重新整理動態
                </Button>
              </div>
              <div className="flex gap-2">{(["post", ...(vlogEnabled ? ["vlog"] as const : [])] as const).map(type => <Button key={type}
                variant={postType === type ? "default" : "outline"} disabled={loading}
                onClick={() => {setPostType(type); void run(gen => readPosts(gen, selected, type));}}>{type === "post" ? "動態" : "Vlog"}</Button>)}</div>
              <p className="text-sm text-muted-foreground">顯示目前類型最新 20 則已發布內容，可測試閱讀、按讚及收藏。{vlogEnabled && "Vlog 使用 HTTPS 雲端預覽。"}</p>
              {posts.map((p) => (
                <button
                  disabled={loading}
                  key={p.contentId}
                  className="block w-full rounded-xl border p-4 text-left"
                  onClick={() => read(p.contentId)}
                >
                  {p.title || p.preview?.slice(0, 40) || "照片動態"}
                </button>
              ))}
              {!loading && posts.length === 0 && (
                <p>還沒有已發布的文字動態。</p>
              )}
              {post && (
                <article className="space-y-5 rounded-xl border bg-card p-5">
                  {post.title && (
                    <h3 className="break-words text-xl">{post.title}</h3>
                  )}
                  {vlogEnabled && post.type === "vlog" && post.video && <ArtistVlogPreview artistId={selected.artistId} contentId={post.contentId}
                    assetId={post.video.assetId} revisionId={post.revisionId} ready={post.video.status === "ready"} />}
                  <p className="whitespace-pre-wrap break-words">{post.body}</p>
                  <div className="flex flex-wrap gap-3">
                    <Button
                      aria-pressed={post.liked}
                      disabled={loading}
                      onClick={() => interact("contentLike", !post.liked)}
                    >
                      {post.liked ? "取消讚" : "按讚"}
                    </Button>
                    <Button
                      variant="outline"
                      aria-pressed={post.saved}
                      disabled={loading}
                      onClick={() => interact("contentSave", !post.saved)}
                    >
                      {post.saved ? "取消收藏" : "收藏"}
                    </Button>
                  </div>
                </article>
              )}
            </section>
          )}
        </>
      )}
      {loading && <p role="status">處理中…</p>}
      {error && (
        <p role="alert" className="rounded border border-destructive p-3">
          {error}
        </p>
      )}
    </main>
  );
}
